import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNavigation,
  CarouselNextButton,
  CarouselPrevButton,
} from "@aleksundshantu.com/aunds-utils-react/primitives/carousel";
import { Section } from "@/components/Section/Section";
import { SingleImageSliderOptionsType } from "@/types/ModuleOptionsType";
import { Modul_Einzelbild_Slider } from "@/wp/types/acf-module-types";
import styles from "./SingleImageSlider.module.scss";
import { columnSize } from "@/utils/layout/columnSize";
import { useEffect, useRef, useState } from "react";
import { HeadlineV2 } from "@/components/Headline/HeadlineV2";
import { MediaItem } from "@/components/MediaItem/MediaItem";

interface SingleImageSliderProps {
  data: Modul_Einzelbild_Slider;
  options: SingleImageSliderOptionsType;
}

export const SingleImageSlider = ({
  data,
  options,
}: SingleImageSliderProps) => {
  const { media_format, card_width, slider_options, box } = options;

  const { show_card_box, show_border_radius, show_box_shadow } = box;

  const { headline, headline_html_tag, overline, subline, elements } =
    data.single_image_slider;

  const {
    background_color_class,
    id,
    module_width,
    section_margin_class,
    section_padding_class,
    headline_text_align_class,
    image_keep_ratio,
  } = data.layout;

  const [imageCaptionHeight, setImageCaptionHeight] = useState(0);
  const captionRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const cardColumns =
    module_width == "narrow-frame" ? columnSize(card_width) : "is-12";

  useEffect(() => {
    const heights = captionRefs.current.map((el) => {
      if (!el) return 0;
      const rect = el.getBoundingClientRect();
      const styles = window.getComputedStyle(el);
      const marginTop = parseFloat(styles.marginTop);
      const marginBottom = parseFloat(styles.marginBottom);
      return rect.height + marginTop + marginBottom;
    });

    const maxHeight = Math.max(...heights);
    if (maxHeight > imageCaptionHeight) {
      setImageCaptionHeight(maxHeight);
    }
  }, [captionRefs, imageCaptionHeight]);

  return (
    <Section
      id={id}
      columnsClassName="is-centered is-multiline"
      frameless={module_width === "frameless"}
      className={`${styles["single-image-slider"]} is-centered ${background_color_class} ${section_margin_class} ${section_padding_class}`}
    >
      <div className={`column ${cardColumns} is-relative `}>
        {headline && (
          <HeadlineV2
            headline_html_tag={headline_html_tag}
            headline={headline}
            extraClassName={headline_text_align_class}
            overline={overline}
            subline={subline}
            disableAnimation
          />
        )}

        <Carousel
          autoplay={slider_options.has_autoplay}
          loop={slider_options.has_looping}
        >
          <CarouselContent
            spacing="0px"
            extraClasses={`${styles["carousel-content"]} `}
          >
            {(elements ? elements : []).map(({ image, caption }, index) => {
              return (
                <CarouselItem
                  key={index}
                  itemWidth={"100%"}
                  className={`${styles["carousel-item"]}`}
                >
                  <article
                    className={`${styles["carousel-item-content"]} columns is-flex-direction-column is-centered is-gapless  m-0 ${show_card_box ? "is-box" : ""} ${show_border_radius && show_card_box ? "has-border-radius" : ""} ${show_box_shadow && show_card_box ? "has-box-shadow" : ""} ${show_card_box && image_keep_ratio ? "p-3" : ""}`}
                  >
                    {image && (
                      <MediaItem
                        image_desktop={image}
                        sizes="100%"
                        priority={true}
                        mediaFormat={
                          image_keep_ratio ? undefined : media_format
                        }
                        objectFit={image_keep_ratio ? "contain" : "cover"}
                        className="has-border-radius"
                      />
                    )}
                    {caption && (
                      <p
                        ref={(el) => {
                          {
                            captionRefs.current[index] = el;
                          }
                        }}
                        className={`${styles["image-caption"]} ${image_keep_ratio ? "pt-3" : "p-3"}`}
                        dangerouslySetInnerHTML={{ __html: caption }}
                      />
                    )}
                  </article>
                </CarouselItem>
              );
            })}
          </CarouselContent>

          {slider_options.slider_navigation == "only-navigation" ? (
            <>
              <div
                className={`${styles["embla-navigation"]}`}
                style={{
                  ["--y-offset" as string]: `${imageCaptionHeight}px`,
                }}
              >
                <CarouselPrevButton />
                <CarouselNextButton />
              </div>
            </>
          ) : (
            <CarouselNavigation type={slider_options.slider_navigation} />
          )}
        </Carousel>
      </div>
    </Section>
  );
};
